import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { InputSwitch } from 'primereact/inputswitch';
import { Message } from 'primereact/message';
import { makeRequest2 } from '../../../../../../utils/makeRequest';
import { toastify } from '../../../../../../utils/toast';
import { useFacilityStore } from '../../../../../../app/z-store/store';
import { useNavigate } from 'react-router-dom';

const InvoicesTab = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 0,
    });
    const [statusFilter, setStatusFilter] = useState(null);
    const [yearMonthFilter, setYearMonthFilter] = useState(null);

    // Bulk Actions State
    const [bulkActionVisible, setBulkActionVisible] = useState(false);
    const [bulkActionType, setBulkActionType] = useState(null);
    const [bulkActionLoading, setBulkActionLoading] = useState(false);
    const [bulkActionData, setBulkActionData] = useState({
        yearMonth: '',
        sendSms: true,
        sendEmail: true,
        daysOverdueMin: 1
    });

    const selectedFacilityId = useFacilityStore((state) => state.facilityId);
    const dt = useRef(null);
    const toast = useRef(null);
    const searchTimeoutRef = useRef(null);

    const statusOptions = [
        { label: 'All Statuses', value: null },
        { label: 'Paid', value: 'Paid' },
        { label: 'Partially Paid', value: 'Partially Paid' },
        { label: 'Cancelled', value: 'Cancelled' },
        { label: 'Overdue', value: 'Overdue' },
        { label: 'Unpaid', value: 'Unpaid' }
    ];

    const generateYearMonthOptions = () => {
        const options = [{ label: 'All Months', value: null }];
        const now = new Date();
        for (let i = 0; i < 12; i++) {
            const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const yearMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
            options.push({ label, value: yearMonth });
        }
        return options;
    };

    const yearMonthOptions = generateYearMonthOptions();

    // Bulk action options for dropdown
    const bulkActionOptions = [
        { label: 'Generate Invoices', value: 'generate', icon: 'ti ti-receipt' },
        { label: 'Send Notifications', value: 'notifications', icon: 'ti ti-bell' },
        { label: 'Send Overdue Reminders', value: 'reminders', icon: 'ti ti-alert-circle' }
    ];

    useEffect(() => {
        if (selectedFacilityId) {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
            searchTimeoutRef.current = setTimeout(() => {
                fetchInvoices();
            }, 500);
        }
    }, [selectedFacilityId, lazyParams, statusFilter, yearMonthFilter, searchTerm]);

    const fetchInvoices = async () => {
        try {
            setLoading(true);
            const skip = lazyParams.first;
            const limit = lazyParams.rows;
            let queryParams = `limit=${limit}&skip=${skip}`;

            if (statusFilter) queryParams += `&status=${statusFilter}`;
            if (yearMonthFilter) queryParams += `&yearMonth=${yearMonthFilter}`;
            if (searchTerm) queryParams += `&searchTerm=${encodeURIComponent(searchTerm)}`;

            const response = await makeRequest2(
                `/api/app/water-invoices/${selectedFacilityId}?${queryParams}`,
                'GET'
            );

            if (response.success && response.data && response.data.data) {
                const invoicesData = response.data.data.invoices || [];
                setInvoices(invoicesData);
                setTotalRecords(response.data.data.totalCount || 0);
            } else {
                toastify('Failed to fetch water invoices', 'error');
            }
        } catch (error) {
            console.error('Error fetching invoices:', error);
            toastify('Error loading water invoices', 'error');
        } finally {
            setLoading(false);
        }
    };

    const onPage = (event) => {
        setLazyParams(event);
    };

    const handleSearchChange = (value) => {
        setSearchTerm(value);
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        setLazyParams(prev => ({
            ...prev,
            first: 0,
            page: 0
        }));
    };

    const handleStatusChange = (value) => {
        setStatusFilter(value);
        setLazyParams(prev => ({
            ...prev,
            first: 0,
            page: 0
        }));
    };

    const handleYearMonthChange = (value) => {
        setYearMonthFilter(value);
        setLazyParams(prev => ({
            ...prev,
            first: 0,
            page: 0
        }));
    };

    // Bulk Actions Handlers
    const handleBulkActionSelect = (actionType) => {
        setBulkActionType(actionType);
        setBulkActionData({
            yearMonth: '',
            sendSms: true,
            sendEmail: true,
            daysOverdueMin: 1
        });
        setBulkActionVisible(true);
    };

    const handleBulkActionSubmit = async () => {
        if (!bulkActionData.yearMonth) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please select a month',
                life: 3000
            });
            return;
        }

        setBulkActionLoading(true);

        try {
            let endpoint = '';
            let payload = {};
            let successMessage = '';

            switch (bulkActionType) {
                case 'generate':
                    endpoint = `/api/app/analog-meters/trigger-billing/${selectedFacilityId}`;
                    payload = {
                        yearMonth: bulkActionData.yearMonth,
                        sendNotifications: bulkActionData.sendSms || bulkActionData.sendEmail
                    };
                    successMessage = 'Billing process triggered successfully';
                    break;

                case 'notifications':
                    endpoint = `/api/app/analog-meters/send-notifications/${selectedFacilityId}`;
                    payload = {
                        yearMonth: bulkActionData.yearMonth,
                        sendSms: bulkActionData.sendSms,
                        sendEmail: bulkActionData.sendEmail
                    };
                    successMessage = 'Notifications queued and sending in the background';
                    break;

                case 'reminders':
                    endpoint = `/api/app/analog-meters/send-overdue-reminders/${selectedFacilityId}`;
                    payload = {
                        yearMonth: bulkActionData.yearMonth,
                        daysOverdueMin: bulkActionData.daysOverdueMin
                    };
                    successMessage = 'Overdue reminders queued and sending in the background';
                    break;

                default:
                    throw new Error('Invalid bulk action type');
            }

            const response = await makeRequest2(endpoint, 'POST', payload, { timeout: 5 * 60 * 1000 });

            if (response.success) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: (bulkActionType !== 'generate' && response.data?.message) || successMessage,
                    life: 3000
                });
                setBulkActionVisible(false);
                await fetchInvoices();
            } else {
                throw new Error(response.error || response.message || 'Operation failed');
            }
        } catch (error) {
            console.error('Error performing bulk action:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to perform bulk action',
                life: 3000
            });
        } finally {
            setBulkActionLoading(false);
        }
    };

    const getBulkActionTitle = () => {
        switch (bulkActionType) {
            case 'generate':
                return 'Generate Invoices';
            case 'notifications':
                return 'Send Notifications';
            case 'reminders':
                return 'Send Overdue Reminders';
            default:
                return 'Bulk Action';
        }
    };

    const getBulkActionDescription = () => {
        switch (bulkActionType) {
            case 'generate':
                return 'Generate water invoices for all customers in the selected month';
            case 'notifications':
                return 'Send invoice notifications to customers for the selected month';
            case 'reminders':
                return 'Send overdue payment reminders to customers with outstanding invoices';
            default:
                return '';
        }
    };

    const statusBodyTemplate = (rowData) => {
        const statusColors = {
            'Unpaid': 'bg-danger text-light',
            'Paid': 'bg-success text-light',
            'Partially Paid': 'bg-warning text-light',
            'Cancelled': 'bg-secondary text-light',
            'Overdue': 'bg-danger text-light'
        };
        return (
            <span className={`badge ${statusColors[rowData.status] || 'bg-secondary text-light'}`}>
                {rowData.status || 'unknown'}
            </span>
        );
    };

    const notificationBodyTemplate = (rowData) => {
        const notifications = rowData.notificationsSent || {};
        const smsStatus = notifications.sms || false;
        const emailStatus = notifications.email || false;

        if (smsStatus && emailStatus) {
            return <span>Email & SMS</span>;
        } else if (emailStatus) {
            return <span>Email</span>;
        } else if (smsStatus) {
            return <span>SMS</span>;
        } else {
            return <span className="text-muted">None</span>;
        }
    };

    const formatNumber = (value) => {
        return new Intl.NumberFormat('en-KE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    const invoiceAmountBodyTemplate = (rowData) => {
        return formatNumber(rowData.charges.totalMonthlyBill);
    };

    const amountPaidBodyTemplate = (rowData) => {
        return formatNumber(rowData.amountPaid);
    };

    const balanceBodyTemplate = (rowData) => {
        const balance = rowData.charges.totalMonthlyBill - rowData.amountPaid;
        return formatNumber(balance);
    };

    const dateBodyTemplate = (rowData, field) => {
        return new Date(rowData[field]).toLocaleDateString();
    };

    const handleView = (invoice) => {
        navigate("/facility/utility_management/invoice_management/invoice_page", {
            state: {
                invoiceId: invoice._id,
                invoiceNumber: invoice.invoiceNumber
            }
        });
    };

    const customerUnitBodyTemplate = (rowData) => {
        const customerName = rowData.CustomerInfo ? rowData.CustomerInfo.fullName : 'N/A';
        const unitName = rowData.unitName || 'N/A';
        return (
            <div>
                <div className="fw-bold">{customerName}</div>
                <div className="small text-muted">{unitName}</div>
            </div>
        );
    };

    const exportCSV = async () => {
        try {
            let queryParams = `limit=10000&skip=0`;
            if (statusFilter) queryParams += `&status=${statusFilter}`;
            if (yearMonthFilter) queryParams += `&yearMonth=${yearMonthFilter}`;
            if (searchTerm) queryParams += `&searchTerm=${encodeURIComponent(searchTerm)}`;

            const response = await makeRequest2(
                `/api/app/water-invoices/${selectedFacilityId}?${queryParams}`,
                'GET'
            );

            if (response.success && response.data && response.data.data) {
                const allInvoices = response.data.data.invoices || [];

                const header = [
                    'Invoice No', 'Invoice Date', 'Customer', 'Unit',
                    'Invoice (KES)', 'Paid Amount (KES)', 'Balance (KES)',
                    'Status', 'Due Date', 'SMS Sent', 'Email Sent'
                ];

                const data = allInvoices.map(invoice => {
                    const notifications = invoice.notificationsSent || {};
                    return [
                        invoice.invoiceNumber,
                        new Date(invoice.dateIssued).toLocaleDateString(),
                        invoice.CustomerInfo ? invoice.CustomerInfo.fullName : 'N/A',
                        invoice.unitName || 'N/A',
                        invoice.charges.totalMonthlyBill,
                        invoice.amountPaid,
                        invoice.charges.totalMonthlyBill - invoice.amountPaid,
                        invoice.status,
                        new Date(invoice.dueDate).toLocaleDateString(),
                        notifications.sms ? 'Yes' : 'No',
                        notifications.email ? 'Yes' : 'No'
                    ];
                });

                const csvContent = [
                    header.join(','),
                    ...data.map(row => row.map(cell =>
                        typeof cell === 'string' && cell.includes(',')
                            ? `"${cell}"`
                            : cell
                    ).join(','))
                ].join('\n');

                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.setAttribute('href', url);
                link.setAttribute('download', `Water_Invoices_${new Date().toISOString().slice(0, 10)}.csv`);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Invoices exported to CSV',
                    life: 3000
                });
            }
        } catch (error) {
            console.error('Error exporting CSV:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to export invoices',
                life: 3000
            });
        }
    };

    const clearFilters = () => {
        setSearchTerm('');
        setStatusFilter(null);
        setYearMonthFilter(null);
        setLazyParams({
            first: 0,
            rows: 10,
            page: 0,
        });
    };

    const bulkActionModalFooter = (
        <div className="flex justify-content-end gap-2">
            <Button
                label="Cancel"
                className="btn btn-outline-secondary"
                onClick={() => setBulkActionVisible(false)}
                disabled={bulkActionLoading}
            />
            <Button
                label="Execute"
                className="btn btn-primary"
                onClick={handleBulkActionSubmit}
                loading={bulkActionLoading}
                disabled={!bulkActionData.yearMonth}
            />
        </div>
    );

    if (!selectedFacilityId) {
        return (
            <div className="card">
                <div className="card-body text-center">
                    Please select a facility first
                </div>
            </div>
        );
    }

    return (
        <div className="card">
            <Toast ref={toast} />
            <div className="card-body">
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                    <div className="d-flex col-md-8 col-xs-12 gap-2 mb-2 mb-md-0">
                        <div className="col-md-4">
                            <input
                                className="form-control"
                                placeholder="Search invoices, customers, units..."
                                value={searchTerm}
                                onChange={(e) => handleSearchChange(e.target.value)}
                            />
                        </div>
                        <div className="col-md-3">
                            <Dropdown
                                value={statusFilter}
                                options={statusOptions}
                                onChange={(e) => handleStatusChange(e.value)}
                                placeholder="Filter by Status"
                                className="w-100"
                            />
                        </div>
                        <div className="col-md-3">
                            <Dropdown
                                value={yearMonthFilter}
                                options={yearMonthOptions}
                                onChange={(e) => handleYearMonthChange(e.value)}
                                placeholder="Filter by Month"
                                className="w-100"
                            />
                        </div>
                        <div className="col-md-2">
                            <Button
                                label="Clear"
                                className="p-button-text btn btn-secondary me-2"
                                type="button"
                                onClick={clearFilters}
                            />
                        </div>
                    </div>
                    <div className="d-flex gap-2">
                        <Dropdown
                            value={null}
                            options={bulkActionOptions}
                            onChange={(e) => handleBulkActionSelect(e.value)}
                            placeholder="Bulk Actions"
                            optionLabel="label"
                            className="p-button-info"
                        />
                        <Button
                            label="CSV"
                            icon="ti ti-file-spreadsheet"
                            className="p-button-success"
                            onClick={exportCSV}
                        />
                    </div>
                </div>

                <div className="dt-responsive table-responsive">
                    <DataTable
                        ref={dt}
                        value={invoices}
                        emptyMessage="No water invoices found."
                        sortMode="multiple"
                        paginator
                        lazy
                        first={lazyParams.first}
                        rows={lazyParams.rows}
                        totalRecords={totalRecords}
                        onPage={onPage}
                        loading={loading}
                        stripedRows
                        tableStyle={{ minWidth: '50rem' }}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                        rowsPerPageOptions={[10, 20, 50, 100]}
                    >
                        <Column field="invoiceNumber" header="Inv No" sortable />
                        <Column
                            field="dateIssued"
                            header="Invoice Date"
                            body={(rowData) => dateBodyTemplate(rowData, 'dateIssued')}
                            sortable
                        />
                        <Column
                            field="CustomerInfo.fullName"
                            header="Customer"
                            body={customerUnitBodyTemplate}
                            sortable
                        />
                        <Column
                            header="Invoice (KES)"
                            body={invoiceAmountBodyTemplate}
                        />
                        <Column
                            field="amountPaid"
                            header="Paid (KES)"
                            body={amountPaidBodyTemplate}
                        />
                        <Column
                            header="Balance (KES)"
                            body={balanceBodyTemplate}
                        />
                        <Column field="status" header="Status" body={statusBodyTemplate} sortable />
                        <Column
                            field="dueDate"
                            header="Due Date"
                            body={(rowData) => dateBodyTemplate(rowData, 'dueDate')}
                        />
                        <Column field="yearMonth" header="Month" sortable />
                        <Column
                            header="Notification Sent"
                            body={notificationBodyTemplate}
                            style={{ width: '120px' }}
                        />
                        <Column
                            header="Actions"
                            body={(rowData) => (
                                <Button
                                    icon="ti ti-eye"
                                    className="p-button-rounded p-button-text"
                                    onClick={() => handleView(rowData)}
                                    tooltip="View Invoice"
                                />
                            )}
                        />
                    </DataTable>
                </div>
            </div>

            {/* Bulk Action Dialog */}
            <Dialog
                visible={bulkActionVisible}
                onHide={() => setBulkActionVisible(false)}
                header={getBulkActionTitle()}
                footer={bulkActionModalFooter}
                style={{ width: '500px' }}
                modal
            >
                <div className="grid p-fluid">
                    <div className="col-12 mb-3">
                        <Message
                            severity="info"
                            text={getBulkActionDescription()}
                            style={{ width: '100%' }}
                        />
                    </div>

                    <div className="col-12">
                        <label htmlFor="yearMonth" className="font-bold mb-2">
                            Select Month <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown
                            id="yearMonth"
                            value={bulkActionData.yearMonth}
                            options={yearMonthOptions.filter(opt => opt.value !== null)}
                            onChange={(e) => setBulkActionData(prev => ({ ...prev, yearMonth: e.value }))}
                            placeholder="Select month"
                        />
                    </div>

                    {bulkActionType === 'generate' && (
                        <>
                            <div className="col-12 mt-3">
                                <div className="flex align-items-center">
                                    <InputSwitch
                                        id="sendNotifications"
                                        checked={bulkActionData.sendSms || bulkActionData.sendEmail}
                                        onChange={(e) => setBulkActionData(prev => ({
                                            ...prev,
                                            sendSms: e.value,
                                            sendEmail: e.value
                                        }))}
                                    />
                                    <label htmlFor="sendNotifications" className="ml-2 font-bold">
                                        Send Notifications After Generation
                                    </label>
                                </div>
                            </div>
                        </>
                    )}

                    {bulkActionType === 'notifications' && (
                        <>
                            <div className="col-12 mt-3">
                                <div className="flex align-items-center mb-2">
                                    <InputSwitch
                                        id="sendSms"
                                        checked={bulkActionData.sendSms}
                                        onChange={(e) => setBulkActionData(prev => ({ ...prev, sendSms: e.value }))}
                                    />
                                    <label htmlFor="sendSms" className="ml-2 font-bold">
                                        Send SMS
                                    </label>
                                </div>
                            </div>
                            <div className="col-12">
                                <div className="flex align-items-center">
                                    <InputSwitch
                                        id="sendEmail"
                                        checked={bulkActionData.sendEmail}
                                        onChange={(e) => setBulkActionData(prev => ({ ...prev, sendEmail: e.value }))}
                                    />
                                    <label htmlFor="sendEmail" className="ml-2 font-bold">
                                        Send Email
                                    </label>
                                </div>
                            </div>
                        </>
                    )}

                    {bulkActionType === 'reminders' && (
                        <div className="col-12 mt-3">
                            <label htmlFor="daysOverdue" className="font-bold mb-2">
                                Minimum Days Overdue
                            </label>
                            <input
                                type="number"
                                id="daysOverdue"
                                className="form-control"
                                value={bulkActionData.daysOverdueMin}
                                onChange={(e) => setBulkActionData(prev => ({
                                    ...prev,
                                    daysOverdueMin: parseInt(e.target.value) || 1
                                }))}
                                min="1"
                            />
                            <small className="text-muted">
                                Only send reminders for invoices overdue by at least this many days
                            </small>
                        </div>
                    )}
                </div>
            </Dialog>
        </div>
    );
};

export default InvoicesTab;